import { Box, Typography, Avatar, IconButton, Divider, Tooltip } from '@mui/material';
import { useMemo, useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import type { CommentDto, MentionUser } from '../types';
import { formatDistanceToNow } from 'date-fns';
import { CommentEditor } from './CommentEditor';
import { resolveMediaPathsStandalone } from '../../detail-form/hooks/useResolvedMediaSync';
import {
  ORIGINAL_MEDIA_HREF_ATTR,
  ORIGINAL_MEDIA_SRC_ATTR,
  RESOLVED_MEDIA_ATTR,
  extractAppMediaPath,
} from '../../detail-form/richTextMediaHelpers';
import { useProbeProfilePhotoQuery } from '../../media/api/mediaProbeApi';
import { buildProfilePhotoThumbnailUrl } from '../../profile-photo/profilePhotoThumbnailUrl';
import { useAuthenticatedImage } from '../../../hooks/useAuthenticatedImage';

const commentResolveCache = new Map<string, string>();
const resolverSafeAttrs = [
  'data-user_id',
  ORIGINAL_MEDIA_SRC_ATTR,
  ORIGINAL_MEDIA_HREF_ATTR,
  RESOLVED_MEDIA_ATTR,
];

interface CommentItemProps {
  comment: CommentDto;
  isEditing?: boolean;
  isSaving?: boolean;
  availableUsers?: MentionUser[];
  uploadContextId: string | number;
  onEdit?: (id: number) => void;
  onCancelEdit?: () => void;
  onSaveEdit?: (id: number, content: string, tags: string[]) => void;
  onDelete?: (id: number) => void;
}

export function CommentItem({
  comment,
  isEditing,
  isSaving,
  availableUsers = [],
  uploadContextId,
  onEdit,
  onCancelEdit,
  onSaveEdit,
  onDelete,
}: CommentItemProps) {
  const isOwner = comment.IsAuthor;
  const profilePhotoQuery = useProbeProfilePhotoQuery(comment.UserId, {
    skip: !comment.UserId,
  });
  const [failedPhotoUrl, setFailedPhotoUrl] = useState<string | null>(null);
  const originalPhotoUrl = profilePhotoQuery.data?.resolvedUrl ?? null;
  const profilePhotoUrl = buildProfilePhotoThumbnailUrl(
    comment.UserId,
    Boolean(originalPhotoUrl),
    originalPhotoUrl
  );
  const authenticatedPhotoUrl = useAuthenticatedImage(profilePhotoUrl);
  const displayPhotoUrl =
    failedPhotoUrl === profilePhotoUrl ? undefined : (authenticatedPhotoUrl ?? undefined);

  const sanitized = useMemo(() => {
    try {
      return DOMPurify.sanitize(comment.CommentContent || '', {
        ADD_ATTR: resolverSafeAttrs,
      });
    } catch (_e) {
      return '';
    }
  }, [comment.CommentContent]);

  const [resolvedHtml, setResolvedHtml] = useState<string>(sanitized);

  const commentNode = useMemo(
    () => (
      <Box sx={{ mb: 2 }}>
        <Divider sx={{ mb: 2 }} />
        <Box sx={{ display: 'flex' }}>
          <Avatar
            src={displayPhotoUrl}
            imgProps={{ onError: () => setFailedPhotoUrl(profilePhotoUrl) }}
            sx={{ mr: 2, bgcolor: 'primary.main', flexShrink: 0 }}
          >
            {comment.Initials ||
              (comment.UserName ? comment.UserName.charAt(0).toUpperCase() : 'U')}
          </Avatar>
          <Box sx={{ width: '100%', minWidth: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {comment.UserName || 'Unknown User'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatDistanceToNow(new Date(comment.CreatedAt), { addSuffix: true })}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {isOwner && !isEditing && (
                  <>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => onEdit?.(comment.CommentId)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => onDelete?.(comment.CommentId)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Box>
            </Box>

            {isEditing ? (
              <Box sx={{ mt: 1 }}>
                <CommentEditor
                  initialContent={comment.CommentContent}
                  onCancel={onCancelEdit}
                  availableUsers={availableUsers}
                  isSubmitting={isSaving}
                  uploadContextId={uploadContextId}
                  onSend={(content, tags) => onSaveEdit?.(comment.CommentId, content, tags)}
                  submitLabel="Save"
                />
              </Box>
            ) : (
              <Box
                sx={{
                  mt: 0.5,
                  typography: 'body2',
                  '& img': { maxWidth: '100%', borderRadius: 1 },
                }}
              >
                <span dangerouslySetInnerHTML={{ __html: resolvedHtml }} />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    ),
    [
      isOwner,
      isEditing,
      resolvedHtml,
      availableUsers,
      isSaving,
      uploadContextId,
      onEdit,
      onCancelEdit,
      onSaveEdit,
      onDelete,
      comment.CommentId,
      comment.Initials,
      comment.UserName,
      comment.CreatedAt,
      comment.CommentContent,
      displayPhotoUrl,
      profilePhotoUrl,
    ]
  );

  useEffect(() => {
    let cancelled = false;

    const process = async () => {
      const raw = comment.CommentContent || '';

      // If we've already resolved this exact content, reuse the cached HTML
      if (commentResolveCache.has(raw)) {
        const cached = commentResolveCache.get(raw) as string;
        if (!cancelled) setResolvedHtml(cached);
        return;
      }

      try {
        const container = document.createElement('div');
        container.innerHTML = raw;

        const paths = new Set<string>();

        container.querySelectorAll('img, a').forEach((el) => {
          const attr =
            el.tagName.toLowerCase() === 'img' ? el.getAttribute('src') : el.getAttribute('href');
          const p = extractAppMediaPath(attr ?? '');
          if (p) paths.add(p);
        });

        if (paths.size === 0) {
          const clean = DOMPurify.sanitize(raw, { ADD_ATTR: resolverSafeAttrs });
          if (!cancelled) setResolvedHtml(clean);
          return;
        }

        const resolved = await resolveMediaPathsStandalone(Array.from(paths));

        const map = new Map<string, string>();
        resolved.forEach((item) => {
          if (item.path && item.url) map.set(item.path, item.url);
        });

        container.querySelectorAll('img, a').forEach((el) => {
          const isImg = el.tagName.toLowerCase() === 'img';
          const attrName = isImg ? 'src' : 'href';
          const val = el.getAttribute(attrName) ?? '';
          const p = extractAppMediaPath(val);
          if (p && map.has(p)) {
            el.setAttribute(attrName, map.get(p) as string);
            el.setAttribute(isImg ? ORIGINAL_MEDIA_SRC_ATTR : ORIGINAL_MEDIA_HREF_ATTR, p);
            el.setAttribute(RESOLVED_MEDIA_ATTR, 'true');
          } else if (p) {
            el.setAttribute(attrName, p);
          }
        });

        const out = DOMPurify.sanitize(container.innerHTML, { ADD_ATTR: resolverSafeAttrs });
        // cache resolved result to avoid duplicate work
        try {
          commentResolveCache.set(raw, out);
        } catch (_err) {
          console.warn('Failed to cache resolved comment content', _err);
        }
        if (!cancelled) setResolvedHtml(out);
      } catch (_err) {
        const fallback = DOMPurify.sanitize(comment.CommentContent || '', {
          ADD_ATTR: resolverSafeAttrs,
        });
        try {
          commentResolveCache.set(raw, fallback);
        } catch (_err2) {
          console.warn('Failed to cache resolved comment content', _err2);
        }
        if (!cancelled) setResolvedHtml(fallback);
      }
    };

    void process();

    return () => {
      cancelled = true;
    };
  }, [comment.CommentContent]);

  return commentNode;
}
